import { SpeechRecognitionLanguage } from '../constants/audio.ts';
import { PrepareUploadResponse } from '../utils/audio/audio-recording-uploader.util.ts';

export enum MediaRecorderType {
  GRADING = 'GRADING',
  STORAGE = 'STORAGE',
}

export interface AudioRecordingState {
  isRecording: boolean;
  isHandlingRecording: boolean;
  audioBlobs?: Record<MediaRecorderType, Blob>;
  prepareUploadPromise?: Promise<PrepareUploadResponse>;
  recordingKey?: string;
  duration: number;
  error?: Error;
}

export interface AudioRecordingCallbacks {
  onPartialResult?: (audioData: AudioRecordingPartialResult) => Promise<void>;
}

export interface AudioRecordingPartialResult {
  mediaRecorderType: MediaRecorderType;
  audioBlob: Blob;
  timeStamp: number;
}

export interface AudioRecordingOptions {
  maxDuration?: number;
  silenceDetection?: Partial<SilenceDetectionOptions> & { enabled?: boolean };
  callbacks?: Partial<AudioRecordingCallbacks>;
}

export type AudioRecordingEventCallback = (state: AudioRecordingState) => void;

export enum AudioRecordingErrorType {
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  INITIALIZATION_ERROR = 'INITIALIZATION_ERROR',
  RECORDING_ERROR = 'RECORDING_ERROR',
  NOT_SUPPORTED = 'NOT_SUPPORTED',
  RECORDING_IN_PROGRESS = 'RECORDING_IN_PROGRESS',
  RECORDING_NOT_STARTED = 'RECORDING_NOT_STARTED',
  SILENCE_DETECTION_IN_PROGRESS = 'SILENCE_DETECTION_IN_PROGRESS',
}

export interface AudioRecordingSuccessResult {
  audioBlobs: Record<MediaRecorderType, Blob>;
  recordingKey: string;
  duration: number;
  success: true;
}

export interface AudioRecordingErrorResult {
  error: AudioRecordingErrorType;
  success: false;
}

export type AudioRecordingResult = AudioRecordingSuccessResult | AudioRecordingErrorResult;

export interface SpeechRecognitionState {
  isListening: boolean;
  isFinal: boolean;
  transcript: string;
  interimTranscript: string;
  audioBlobs?: Record<MediaRecorderType, Blob>;
  recordingKey?: string;
  error?: Error;
}

export interface SpeechRecognitionOptions {
  language?: SpeechRecognitionLanguage;
  interimResults?: boolean;
  maxAlternatives?: number;
}

export type SpeechRecognitionCallback = (result: SpeechRecognitionResult) => void;

export interface SpeechRecognitionCallbacks {
  onInterimResult?: SpeechRecognitionCallback;
  onFinalResult?: SpeechRecognitionCallback;
}

export enum SpeechRecognitionErrorType {
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  INITIALIZATION_ERROR = 'INITIALIZATION_ERROR',
  RECOGNITION_ERROR = 'RECOGNITION_ERROR',
  NOT_SUPPORTED = 'NOT_SUPPORTED',
  RECORDING_ERROR = 'RECORDING_ERROR',
  RECORDING_IN_PROGRESS = 'RECORDING_IN_PROGRESS',
  NOT_STARTED = 'NOT_STARTED',
}

export type SpeechRecognitionResult =
  | {
      transcript: string;
      audioBlobs: Record<MediaRecorderType, Blob>;
      recordingKey: string;
      isFinal: true;
      success: true;
    }
  | {
      transcript: string;
      isFinal: false;
      success: true;
    }
  | {
      error: SpeechRecognitionErrorType;
      success: false;
    };

export interface SilenceDetectionOptions {
  silenceThreshold: number;
  silenceDuration: number;
  onSilenceDetected?: () => void | Promise<void>;
}

export enum SilenceDetectionErrorType {
  INITIALIZATION_ERROR = 'INITIALIZATION_ERROR',
  SILENCE_DETECTION_ERROR = 'SILENCE_DETECTION_ERROR',
  ALREADY_INITIALIZED = 'ALREADY_INITIALIZED',
}

export enum AudioPlayerErrorType {
  LETTER_NOT_FOUND = 'LETTER_NOT_FOUND',
  SFX_NOT_FOUND = 'SFX_NOT_FOUND',
  FILE_NOT_FOUND = 'FILE_NOT_FOUND',
  STEP_NOT_FOUND = 'STEP_NOT_FOUND',
}
